import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Image,
  Input,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useState, type KeyboardEvent, type RefObject } from 'react';

import { BowerSpinner } from '../../ui/BowerSpinner';
import { MaterialIcon } from '../../ui/MaterialIcon';
import type { LinkFieldsState } from './useLinkFields';

interface LinkFieldsProps {
  readonly fields: LinkFieldsState;
  readonly isDisabled?: boolean;
  /** Enter in a field submits, as the other create forms do. */
  readonly onSubmit: () => void;
  readonly urlInputRef?: RefObject<HTMLInputElement | null>;
}

const ICON_PX = '20px';

/** The site icon at preview size, or the link glyph when it will not load. */
function SiteIconPreview({ src }: { readonly src: string | null }) {
  const [failed, setFailed] = useState<string | null>(null);
  if (!src || failed === src) {
    return <MaterialIcon name="public" boxSize={ICON_PX} color="text.muted" aria-hidden />;
  }
  return (
    <Image
      src={src}
      alt=""
      boxSize={ICON_PX}
      objectFit="contain"
      referrerPolicy="no-referrer"
      onError={() => setFailed(src)}
    />
  );
}

/**
 * The link form, shared by create and "Edit link": the address (checked as
 * the server will check it), the title (prefilled from the page until the
 * user types), and the icon, the site's or Bower's, each previewed.
 */
export function LinkFields({ fields, isDisabled, onSubmit, urlInputRef }: LinkFieldsProps) {
  const submitOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
    event.preventDefault();
    onSubmit();
  };

  return (
    <VStack spacing={4} align="stretch">
      <FormControl isRequired isInvalid={Boolean(fields.urlError)}>
        <FormLabel>Web address</FormLabel>
        <Input
          ref={urlInputRef}
          type="url"
          inputMode="url"
          placeholder="https://"
          value={fields.url}
          isDisabled={isDisabled}
          onChange={(event) => fields.setUrl(event.target.value)}
          onKeyDown={submitOnEnter}
        />
        <FormErrorMessage>{fields.urlError}</FormErrorMessage>
      </FormControl>
      <FormControl>
        <FormLabel>Title</FormLabel>
        <Input
          value={fields.titleInput}
          placeholder={fields.titlePlaceholder}
          isDisabled={isDisabled}
          onChange={(event) => fields.setTitle(event.target.value)}
          onKeyDown={submitOnEnter}
        />
      </FormControl>
      <FormControl as="fieldset">
        <FormLabel as="legend">Icon</FormLabel>
        <RadioGroup
          value={fields.icon}
          onChange={(value) => fields.setIcon(value === 'site' ? 'site' : 'bower')}
          isDisabled={isDisabled}
        >
          <HStack spacing={6}>
            <Radio value="site" isDisabled={isDisabled || !fields.siteIcon}>
              <HStack spacing={2}>
                <SiteIconPreview src={fields.siteIcon} />
                <Text>Site icon</Text>
              </HStack>
            </Radio>
            <Radio value="bower">
              <HStack spacing={2}>
                <MaterialIcon name="link" boxSize={ICON_PX} gradient="warm" aria-hidden />
                <Text>Bower icon</Text>
              </HStack>
            </Radio>
          </HStack>
        </RadioGroup>
      </FormControl>
      <HStack justify="space-between" minH={8}>
        <Box>
          {fields.isLookingUp && (
            <HStack spacing={2} color="text.secondary" fontSize="sm" role="status">
              <BowerSpinner size="xs" tone="muted" />
              <Text>Looking up the page...</Text>
            </HStack>
          )}
        </Box>
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<MaterialIcon name="refresh" boxSize={4} aria-hidden />}
          isDisabled={isDisabled || !fields.normalizedUrl}
          onClick={fields.refresh}
        >
          Refresh from page
        </Button>
      </HStack>
    </VStack>
  );
}
